import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "./Modal";
import { ShareIcon } from "./ShareIcon";
import { useToast } from "./Toast";
import {
  shareContent,
  isWebShareSupported,
  isClipboardSupported,
  type ShareContent,
  type SharePlatform,
} from "~/lib/share-utils";

interface ShareModalProps {
  content: ShareContent;
  isOpen: boolean;
  onClose: () => void;
}

interface ShareOption {
  platform: SharePlatform;
  label: string;
  description: string;
  color: string;
  available: boolean;
}

export function ShareModal({ content, isOpen, onClose }: ShareModalProps) {
  const [isSharing, setIsSharing] = useState(false);
  const [currentSharingPlatform, setCurrentSharingPlatform] =
    useState<SharePlatform | null>(null);
  const { addToast } = useToast();
  const [shareOptions, setShareOptions] = useState<ShareOption[]>([]);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // 키보드 네비게이션을 위한 포커스 관리
  useEffect(() => {
    if (isOpen && firstButtonRef.current) {
      // 모달이 열릴 때 첫 번째 버튼에 포커스
      setTimeout(() => {
        firstButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key === "Tab") {
        // Tab 키 네비게이션 관리
        const focusableElements = modalRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    // 클라이언트에서만 실행되도록 함
    if (typeof window !== "undefined") {
      setShareOptions([
        {
          platform: "facebook",
          label: "Facebook",
          description: "페이스북에 공유하기",
          color: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white",
          available: true,
        },
        {
          platform: "twitter",
          label: "X (Twitter)",
          description: "X에 공유하기",
          color: "bg-black hover:bg-gray-800 focus:ring-gray-500 text-white",
          available: true,
        },
        {
          platform: "instagram",
          label: "Instagram",
          description: "인스타그램용 텍스트 복사",
          color:
            "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 focus:ring-purple-500 text-white",
          available: isClipboardSupported(),
        },
        {
          platform: "clipboard",
          label: "링크 복사",
          description: "클립보드에 링크 복사",
          color: "bg-gray-600 hover:bg-gray-700 focus:ring-gray-500 text-white",
          available: isClipboardSupported(),
        },
        {
          platform: "native",
          label: "기기 공유",
          description: "기기의 공유 기능 사용",
          color:
            "bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white",
          available: isWebShareSupported(),
        },
      ]);
    }
  }, []);

  const handleShare = async (platform: SharePlatform) => {
    setIsSharing(true);
    setCurrentSharingPlatform(platform);

    try {
      const result = await shareContent(platform, content);

      if (result.success) {
        addToast({
          type: "success",
          message: result.message || "공유가 완료되었습니다.",
          duration: 3000,
        });

        // Instagram과 클립보드 복사의 경우 모달을 닫지 않음 (사용자가 확인할 수 있도록)
        if (platform !== "instagram" && platform !== "clipboard") {
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } else {
        addToast({
          type: "error",
          message: result.message || "공유에 실패했습니다.",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("공유 오류:", error);
      addToast({
        type: "error",
        message: "공유 중 오류가 발생했습니다.",
        duration: 5000,
      });
    } finally {
      setIsSharing(false);
      setCurrentSharingPlatform(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} showCloseButton={false}>
      <div
        ref={modalRef}
        className="w-full max-w-md mx-auto"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        aria-describedby="share-modal-description"
      >
        <div className="flex items-center justify-between mb-6">
          <h3
            id="share-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            공유하기
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1"
            aria-label="공유 모달 닫기"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <p id="share-modal-description" className="sr-only">
          다양한 플랫폼으로 콘텐츠를 공유할 수 있습니다. 키보드의 Tab 키로
          옵션을 탐색하고 Enter 키로 선택하세요.
        </p>

        {/* 공유할 콘텐츠 미리보기 */}
        <div
          className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
          role="region"
          aria-label="공유할 콘텐츠 미리보기"
        >
          <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
            {content.title}
          </h4>
          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
            {content.description}
          </p>
          {content.imageUrl && (
            <div className="mt-3">
              <img
                src={content.imageUrl}
                alt={`${content.title} 미리보기 이미지`}
                className="w-full h-24 object-cover rounded"
                loading="lazy"
              />
            </div>
          )}
        </div>

        {/* 공유 옵션 */}
        <div className="space-y-3" role="group" aria-label="공유 옵션">
          {shareOptions
            .filter((option) => option.available)
            .map((option, index) => (
              <button
                key={option.platform}
                ref={index === 0 ? firstButtonRef : undefined}
                onClick={() => handleShare(option.platform)}
                disabled={isSharing}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 ${option.color}`}
                aria-describedby={`share-${option.platform}-desc`}
                aria-pressed={currentSharingPlatform === option.platform}
              >
                <ShareIcon platform={option.platform} size="md" />
                <div className="flex-1 text-left">
                  <div className="font-medium">{option.label}</div>
                  <div
                    id={`share-${option.platform}-desc`}
                    className="text-sm opacity-90"
                  >
                    {option.description}
                  </div>
                </div>
                {isSharing && currentSharingPlatform === option.platform && (
                  <div
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    role="status"
                    aria-label="공유 중"
                  />
                )}
              </button>
            ))}
        </div>

        {shareOptions.filter((option) => option.available).length === 0 && (
          <div
            className="text-center py-8 text-gray-500 dark:text-gray-400"
            role="alert"
          >
            <p>사용 가능한 공유 옵션이 없습니다.</p>
            <p className="text-sm mt-1">
              브라우저가 공유 기능을 지원하지 않습니다.
            </p>
          </div>
        )}

        {/* 도움말 */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div
            className="text-xs text-gray-500 dark:text-gray-400 text-center space-y-1"
            role="note"
            aria-label="공유 도움말"
          >
            <p>
              <strong>Instagram:</strong> 텍스트를 복사한 후 앱에서 붙여넣기
              해주세요.
            </p>
            <p>
              <strong>키보드 사용:</strong> Tab 키로 옵션 탐색, Enter 키로 선택,
              Escape 키로 닫기
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
